import type { Content } from '@tiptap/react';
import * as React from 'react';

import { GUID, TaskView } from '@ndvr/customer-api';

import { MinimalTiptapEditor } from '@/components/minimal-tiptap';
import { MentionSuggestionItem } from '@/components/minimal-tiptap/extensions';
import { Button } from '@/components/ui/button';
import { convertPlainTextToHtml, hasVisibleTextContent } from 'utils';
import { Toast } from 'widgets';

import { useCreateTaskComment } from '../task-queries';
import { extractMentionsFromHtml } from '../task-utils';

const getEditorStringValue = (value: Content) => (typeof value === 'string' ? value : '');

export interface TaskCommentSectionProps {
    taskGuid: GUID<TaskView>;
    draft: string;
    onDraftChange: (value: string) => void;
    mentionSuggestions: MentionSuggestionItem[];
}

export const TaskCommentSection = ({ taskGuid, draft, onDraftChange, mentionSuggestions }: TaskCommentSectionProps) => {
    const createTaskComment = useCreateTaskComment();
    const [editorKey, setEditorKey] = React.useState(0);

    const handleSubmit = async () => {
        if (!hasVisibleTextContent(draft)) return;
        try {
            await createTaskComment.mutateAsync({
                taskGuid,
                request: {
                    content: draft,
                    mentions: extractMentionsFromHtml(draft),
                },
            });
            onDraftChange('');
            setEditorKey((k) => k + 1);
        } catch (error) {
            Toast.forError(error as Error, 'Failed to post comment');
        }
    };

    return (
        <div className="space-y-3">
            <div className="space-y-1">
                <h3 className="text-sm font-medium">Add Comment</h3>
                <p className="text-muted-foreground text-xs">
                    Comments and task activity appear together in the timeline below.
                </p>
            </div>
            <MinimalTiptapEditor
                key={editorKey}
                mentions={mentionSuggestions}
                value={convertPlainTextToHtml(draft)}
                onChange={(nextValue) => onDraftChange(getEditorStringValue(nextValue))}
                className="w-full"
                editorContentClassName="p-5"
                output="html"
                placeholder="Write a comment..."
                editable={!createTaskComment.isPending}
                editorClassName="focus:outline-hidden"
            />
            <div className="flex justify-end">
                <Button
                    size="sm"
                    onClick={handleSubmit}
                    disabled={!hasVisibleTextContent(draft) || createTaskComment.isPending}
                >
                    {createTaskComment.isPending ? 'Posting…' : 'Post Comment'}
                </Button>
            </div>
        </div>
    );
};
